import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { CSSTransition } from 'react-transition-group';
import { useButton, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import useOffers from '~Hooks/useOffers';
import percentIcon from '../../../../../../Assets/icons/percent_discount.svg';
import arrowForwardIcon from '../../../../../../Assets/icons/arrow_forward.svg';
import { useExpressCart } from '../../../../Providers/ExpressCartProvider';
import useSessionActive from '../../../../Hooks/useSessionActive';
import PromoCodeModal from './PromoCodeModal';

/** How long the row takes to fade in or out - the CSSTransition below uses the same number. */
const FADE_MS = 250;

/**
 * One row at the bottom of the cart, right above the checkout button in the
 * landscape panel: "Have a promo code?" with an arrow. Fades
 * in on mount like the suggestions, and fades out through the exit classes
 * when a code gets applied, instead of vanishing.
 */
const Row = styled.div`
  flex: 0 0 auto;
  /* Same side inset as the checkout button under it; the bottom gap is the space between them. */
  margin: 0 20px 12px;
  padding: 20px 22px;
  border-radius: 18px;
  border: 2px solid var(--eposBorderColor);
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  user-select: none;
  color: var(--eposText);
  animation: eposFadeIn ${FADE_MS}ms ease-out both;
  transition: transform 0.15s ease-out, opacity ${FADE_MS}ms ease-out;

  &.is-touching {
    transform: scale(0.98);
  }

  &.epos-promo-exit-active,
  &.epos-promo-exit-done {
    animation: none;
    opacity: 0;
  }

  @keyframes eposFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 19px;
  font-weight: 700;
`;

/**
 * Promo code entry point in the cart. Rendered only when the company has a
 * code that is active right now and none is applied yet - an applied code
 * cannot be removed, and shows up on the lines it discounts instead of here.
 * The modal is mounted on demand and lives in a portal, so the drawer itself
 * never renders for anything typed in it.
 */
const PromoCode: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-promo-code');
  const offers = useOffers();
  const expressCart = useExpressCart();

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => {
    logger.info('Promo code modal opened');
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => setOpen(false), []);

  /** The idle timeout (or start over) must not leave the modal up for the next shopper. */
  const sessionActive = useSessionActive();
  useEffect(() => {
    if (!sessionActive) {
      logger.info('Session ended - closing the promo code modal');
      setOpen(false);
    }
  }, [sessionActive]);

  const { touching, disabled, disabledToast, ...rowButton } = useButton({ onClick: handleOpen });

  const rowRef = useRef<HTMLDivElement>(null);
  const visible = !expressCart.appliedDiscount && offers.hasActivePromoCodes;

  return (
    <>
      {/* Kept mounted through the fade-out, then removed. */}
      <CSSTransition
        in={visible}
        nodeRef={rowRef}
        timeout={FADE_MS}
        classNames="epos-promo"
        mountOnEnter
        unmountOnExit>
        <Row
          {...rowButton}
          ref={rowRef}
          role="button"
          className={touching ? 'is-touching' : undefined}
          data-testid="epos-promo-row">
          <Icon src={percentIcon} color="var(--eposText)" width="22px" height="22px" />
          <Label>{i18n('Express.PromoCode.Have')}</Label>
          <Icon src={arrowForwardIcon} color="var(--eposText)" width="18px" height="18px" />
        </Row>
      </CSSTransition>
      <PromoCodeModal open={open} onClose={handleClose} />
    </>
  );
};

export default memo(PromoCode);
